import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useQuery from "../../../hooks/useQuery";
import { message, Modal } from "antd";
import CustomLayout from "../../Layout/CustomLayout";
import PharmacySideMenu from "../../Layout/CustomLayout/PharmacySideMenu";
import PatientDetailsCard from "./PatientDetailsCard";
import MedicinesConsumablesCard from "./MedicinesConsumablesCard";
import SaleEntryBillingCard from "./SaleEntryBillingCard";
/* Price History is no longer a panel of its own - it opens under the line it
   belongs to, inside the Medicines & Consumables card, as the mockup has it.
   PriceHistoryCard, the side panel it used to be, is left in the tree
   unreferenced. */
import RecordPaymentModal from "./RecordPaymentModal";
import DiscardEntryModal from "./DiscardEntryModal";
import AuditLogDrawer, { AUDIT_ENTITIES } from "../common/AuditLogDrawer";
import GstSummaryTable from "../common/GstSummaryTable";
import { useIsPharmacyAdmin } from "../Users/PharmacyUsers";
import { canCancelDocument } from "../common/cancelWindow";
import CgIcon from "../common/CgIcon";
import { formatRupees } from "../common/money";
import styles from "./saleEntry.module.css";
import { toId } from "../common/ids";
import {
  getPharmacyOrderApi,
  cancelPharmacyOrderApi,
  canPrintOrderReceipt,
  printPharmacyOrderReceipt,
} from "../../../services/pharmacyOrders.services";
import PharmacyGlobalSearch from "../common/PharmacyGlobalSearch";
import {
  generatePharmacyBillApi,
  getPharmacyBillApi,
  recordPharmacyPaymentApi,
} from "../../../services/pharmacyBilling.services";


const SaleEntryPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const query = useQuery();
  /*
   * Return to the filter this entry was opened from. The Orders list sets
   * ?from=<bucket> on its view action, so leaving lands back on the same set of
   * orders rather than on the unfiltered list.
   */
  const ORDER_BUCKETS = [
    "pending",
    "partially-dispensed",
    "dispensed",
    "completed",
    "cancelled",
  ];
  const ordersListPath = ORDER_BUCKETS.includes(query?.from)
    ? `/pharmacy/orders?tab=${query.from}`
    : "/pharmacy/orders";
  const isNew = !orderId || orderId === "new";
  const location = useLocation();
  /*
   * A patient chosen in the header search arrives here as navigation state. It
   * seeds the form as an order-shaped object with no id, so the page still
   * treats this as a new entry - the order is only created once a medicine is
   * added, exactly as when starting from scratch.
   */
  const [order, setOrder] = useState(() =>
    isNew && location.state?.prefill ? location.state.prefill : null
  );
  const [bill, setBill] = useState(null);
  const [preview, setPreview] = useState(null);
  /* The lines as the bill card works them out - slab, taxable value and the
     heads the tax divides into. The raw lines above carry none of that. */
  const [gstLines, setGstLines] = useState([]);
  /*
   * The id this screen has actually finished reading.
   *
   * Everything on the bar is decided from the order's status, and an order
   * still on its way has no status - so nothing is offered until the read for
   * the id in the address has come back. Tracked as the id itself rather than
   * compared against `order.id`: if the two ever disagreed, the comparison
   * would hide the bar for good, and a missing Cancel is worse than a
   * flashing one.
   */
  const [loadedId, setLoadedId] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [printing, setPrinting] = useState(false);
  /* The line the price history panel in the 30% column is following. */
  /*
   * What the action bar shows. `bumpKey` remounts the figure so it can flash
   * whenever it changes - it is the only feedback an edit down in the line grid
   * gets over on this side of the screen.
   */
  const [payable, setPayable] = useState({ amount: 0, lines: 0, billed: false });
  /*
   * What the action bar offers. An IP sale is not settled at the counter - it
   * is submitted and lands on the admission's own bill - so it reads "Submit".
   * Everything else is paid for on the spot, and says so.
   */
  const [saleType, setSaleType] = useState(order?.sale_type || "IP");
  /*
   * Who the price history panel asks about. Only a PATIENT sale carries this on
   * the order - an IP or OP order is keyed by the admission - so the patient
   * details card resolves it from the admission it has already read back and
   * reports it here.
   */
  const [patientKey, setPatientKey] = useState(null);
  /*
   * The open bill this sale is being added to, when one was picked. What is
   * already on it leads the billing card and counts toward the payable figure,
   * because that is the amount being asked for at the counter.
   */
  const [carriedBill, setCarriedBill] = useState(null);
  /* Reported as one object, so a re-render that changed nothing does not
     re-fetch the history behind it. */
  const handlePatientChange = (next) =>
    setPatientKey((prev) =>
      prev?.id === next?.id && prev?.name === next?.name && prev?.mrn === next?.mrn
        ? prev
        : next
    );
  const [bumpKey, setBumpKey] = useState(0);
  /*
   * Everything but an IP sale is paid for at the counter, so the bar's action
   * opens the Record Payment dialog and the bill is raised from inside it. An IP
   * sale lands on the admission's own bill and is submitted straight away.
   */
  const [payOpen, setPayOpen] = useState(false);
  /* Leaving with unsaved work asks first - see DiscardEntryModal. */
  const [discardOpen, setDiscardOpen] = useState(false);
  /* What has happened to this order, read beside it - Pharmacy Admins only,
     which is who the audit endpoint answers. */
  const isPharmacyAdmin = useIsPharmacyAdmin();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [saving, setSaving] = useState(false);
  /*
   * What the line grid is holding that the server has not been told about.
   * `lines` counts every line on screen, saved or not; `unsaved` is whether any
   * of it would be lost by leaving now.
   */
  const [draft, setDraft] = useState({ lines: 0, unsaved: false });
  const handleDraftChange = (next) =>
    setDraft((prev) =>
      prev.lines === next.lines && prev.unsaved === next.unsaved ? prev : next
    );
  const medsRef = useRef(null);
  const patientRef = useRef(null);
  const billingRef = useRef(null);
  /*
   * Set only when this page created the order during this visit. An order
   * opened from the Orders list is never this, so leaving that page can never
   * remove it - see leaveEntry.
   */
  const createdHereRef = useRef(false);

  const refreshOrder = () => {
    const id = order?.id || (!isNew ? orderId : null);
    if (!id) {
      setRefresh((prev) => !prev);
      return;
    }
    getPharmacyOrderApi(id)
      .then((res) => setOrder(res?.data))
      .catch(() => message.error("Failed to load order"));
  };

  useEffect(() => {
    if (isNew) return;

    setOrder((prev) => (String(prev?.id) === String(orderId) ? prev : null));

    getPharmacyOrderApi(orderId)
      .then((res) => setOrder(res?.data))
      .catch(() => message.error("Failed to load order"))
      .finally(() => setLoadedId(String(orderId)));
  }, [orderId, refresh]);

  const refreshBill = () => {
    if (!bill?.id) return;
    getPharmacyBillApi(bill.id)
      .then((res) => setBill(res?.data))
      .catch(() => message.error("Failed to refresh bill"));
  };

  const handlePatientDetailsSaved = (savedOrder) => {
    if (isNew && !order?.id) createdHereRef.current = true;
    setOrder(savedOrder);
    if (isNew) navigate(`/pharmacy/orders/sale-entry/${savedOrder.id}`, { replace: true });
  };


  const CANCELLABLE_STATUSES = ["pending", "partially_dispensed"];
  const canCancelOrder =
    Boolean(order?.id) &&
    !bill &&
    CANCELLABLE_STATUSES.includes(order?.status);

  const askToCancelOrder = () => {
    Modal.confirm({
      title: `Cancel ${order?.order_number || "this order"}?`,
      content: "Are you sure? This cannot be undone.",
      okText: "Yes, cancel it",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: () =>
        cancelPharmacyOrderApi(order.id)
          .then(() => {
            message.success(`${order?.order_number || "Order"} cancelled`);
            navigate(ordersListPath);
          })
          .catch((err) =>
            message.error(
              err?.response?.data?.message || "Failed to cancel the order"
            )
          ),
    });
  };
  const hasWork = !bill && draft.unsaved;
  const isEmptyDraft =
    createdHereRef.current && Boolean(order?.id) && !bill && !(order?.items || []).length;
  const willDiscard = !order?.id || isEmptyDraft;

  const leaveEntry = () => {
    if (!hasWork) {
      discardEntry();
      return;
    }
    setDiscardOpen(true);
  };

  const saveAndLeave = async () => {
    setSaving(true);
    try {
      const saved = await medsRef.current?.saveDraft();
      if (!saved?.id) return;
      message.success("Entry saved. It is waiting in Pending.");
      setDiscardOpen(false);
      navigate(ordersListPath);
    } finally {
      setSaving(false);
    }
  };

  /*
   * Writing a correction back on an order that is already finished with.
   *
   * The order has been billed and the stock has moved - this only rewrites
   * what it says. The screen stays where it is, because the point of
   * correcting a line is to look at it afterwards.
   */
  const saveEdits = async () => {
    setSaving(true);
    try {
      const saved = await medsRef.current?.saveDraft();
      if (!saved?.id) return;
      message.success("Changes saved.");
      refreshOrder();
    } finally {
      setSaving(false);
    }
  };

  const discardEntry = async () => {
    setDiscarding(true);
    try {
      if (isEmptyDraft) {
        await cancelPharmacyOrderApi(order.id).catch(() => {});
      }
      navigate(ordersListPath);
    } finally {
      setDiscarding(false);
    }
  };

  
  const handlePrintReceipt = async () => {
    if (!order?.id) return;
    setPrinting(true);
    try {
      await printPharmacyOrderReceipt(order.id, "detailed");
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to open the receipt"
      );
    } finally {
      setPrinting(false);
    }
  };

  const ensureOrder = async () => {
    if (order?.id) {
      if (order.sale_type === "COUNTER") {
        const saved = await patientRef.current?.ensureSaved();
        return saved || order;
      }
      return order;
    }
    const saved = await patientRef.current?.ensureSaved();
    return saved;
  };

  const handlePayableChange = (next) => {
    setPayable((prev) => {
      if (
        prev.amount === next.amount &&
        prev.lines === next.lines &&
        prev.billed === next.billed
      ) {
        return prev;
      }
      if (prev.amount !== next.amount) setBumpKey((key) => key + 1);
      return next;
    });
  };

  const isIpSale = saleType === "IP";

  
  const isLocked =
    order?.status === "completed" || order?.status === "cancelled";

  /*
   * Whether the lines may still be corrected, on the rule the purchase
   * screens use: a live order any time, a completed one inside the day it was
   * submitted, a Pharmacy Admin whenever. A cancelled one, never.
   *
   * `canCancelDocument` is asked only about a completed order, because it
   * answers for a draft and a completed document and a sale order is neither
   * word - pending and partially dispensed are simply still being worked on.
   */
  const linesEditable =
    isNew ||
    (order?.status !== "cancelled" &&
      (order?.status !== "completed" ||
        canCancelDocument(order, { isAdmin: isPharmacyAdmin })));
  const hasItems = draft.lines > 0;
  const orderKnown = isNew || loadedId === String(orderId);
  /*
   * The bar at the foot.
   *
   * On a live order it carries the whole set - Cancel, and the ways to bill.
   * On one that is finished with it carries nothing until a line is actually
   * corrected, and then only the way to write that correction back: the same
   * shape Purchase Entry takes, where a completed document shows Cancel alone
   * until something changes.
   */
  const showActions = orderKnown && !isLocked && !bill;
  const showSaveEdits =
    orderKnown && isLocked && linesEditable && draft.unsaved;



  const historyPatientId =
    saleType === "COUNTER" ? null : patientKey?.id ?? toId(order?.patient);
  const historyPatientName =
    patientKey?.name || order?.patient_name || order?.counter_sale_customer_name || "";

  /*
   * Raise the bill, and settle it with whatever the dialog collected. Returns
   * whether it got there, so the dialog knows to close: a run that stopped on a
   * missing batch or an empty order leaves it open with the reason on screen.
   */
  const handleGenerateBill = async (payment = null) => {
    setGenerating(true);
    try {
      /*
       * The entry is written here and nowhere earlier: the order, its lines and
       * the figures on them all go up at this point, and what can be issued is
       * issued. A run that stops on a bad quantity or on patient details that
       * will not validate has written nothing and has already said why.
       */
      const dispenseResult = await medsRef.current?.saveAndDispense();
      const ensured = dispenseResult?.order;
      if (!ensured?.id) return false;

      const orderRes = await getPharmacyOrderApi(ensured.id);
      setOrder(orderRes.data);

      const items = orderRes.data?.items || [];
      if (!items.length) {
        message.warning("Add medicines before generating a bill");
        return false;
      }

      /*
       * A line without a batch is refused before anything is written - see
       * saveAndDispense - so nothing reaches here half-dispensed. What is
       * left to catch is an entry that dispensed nothing at all.
       */
      const hasDispensedItems = items.some((item) => item.dispense_qty > 0);
      if (!hasDispensedItems) {
        message.warning(
          "Nothing has gone out on this entry yet. Enter a quantity against a batch before generating a bill."
        );
        return false;
      }

      const discount = billingRef.current?.getDiscount() || { discount_type: "percentage", discount: 0 };
      const billRes = await generatePharmacyBillApi(ensured.id, discount);
      let finalBill = billRes.data;

      if (payment) {
        try {
          await recordPharmacyPaymentApi(finalBill.id, payment);
          const refreshed = await getPharmacyBillApi(finalBill.id);
          finalBill = refreshed.data;
        } catch (err) {
          message.warning(
            err?.response?.data?.message ||
              "Bill generated, but the payment could not be recorded. Record it from the Payment Receipt panel."
          );
        }
      }

      setBill(finalBill);
      message.success(
        payment ? `Bill ${finalBill.bill_number} generated and payment recorded` : `Bill ${finalBill.bill_number} generated`
      );
      return true;
    } catch (err) {
      if (err?.errorFields) return false;
      message.error(err?.response?.data?.message || "Failed to generate bill");
      return false;
    } finally {
      setGenerating(false);
    }
  };

  /* An IP sale is not settled at the counter, so Submit runs straight through;
     everything else collects the money first. */
  const handleBarAction = () => {
    if (isIpSale) {
      handleGenerateBill();
      return;
    }
    setPayOpen(true);
  };

  return (
    <CustomLayout
      headerSearch={<PharmacyGlobalSearch />}
      layoutClassName="reception-layout compact-footer-layout pharmacy-motion"
      sideContent={<PharmacySideMenu />}
      header={
        // The mockup's header carries only search and the account menu; each
        // screen names itself in its own heading. Kept truthy so CustomLayout
        // still renders the bar.
        <></>
      }
      footer={
        showSaveEdits ? (
          <div className={styles.seBar}>
            {/* What is being corrected, and the only thing left to do with
                it. The bar lays its children out in a row and pushes the
                first one's margin to the right, so the note leads and the
                button sits in the corner the primary action always sits in. */}
            <span className={styles.seBarT}>
              <i>Editing</i>
              <b>{order?.order_number || "this order"}</b>
            </span>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnP}`}
              disabled={saving}
              onClick={saveEdits}
            >
              {saving ? "Saving\u2026" : "Save changes"}
            </button>
          </div>
        ) : showActions ? (
          /* The two actions live here and only here, on the bar the layout
             keeps with the bottom of the screen however long the list gets -
             and the running total rides with them. */
          <div className={styles.seBar}>
            <span className={styles.seBarT}>
              <i>Payable</i>
              {/* No bump on the first paint - there was nothing to change from. */}
              <b key={bumpKey} className={`${styles.tnum} ${bumpKey ? styles.seBumped : ""}`}>
                {formatRupees(payable.amount)}
              </b>
              <em>
                {payable.lines} {payable.lines === 1 ? "medicine" : "medicines"}
              </em>
            </span>
            {/* Cancel calls the order off where there is one to call off, and
                is the way out before that - an entry that was never saved has
                nothing on the server to cancel. */}
            <button
              type="button"
              className={`${styles.btn} ${styles.btnR}`}
              onClick={canCancelOrder ? askToCancelOrder : leaveEntry}
            >
              Cancel
            </button>
            {/*
              * Raise the bill and leave it to be settled later.
              *
              * The same run as the primary action, minus the money: it still
              * dispenses and writes the bill, it just does not open the payment
              * dialog, so the bill stands unpaid until someone collects it.
              * Offered only where payment is collected at all - an IP sale is
              * settled on the admission, so its Submit already does this.
              */}
            {!isIpSale && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnG}`}
                disabled={generating || !hasItems}
                title="Raise the bill now and take payment later"
                onClick={() => handleGenerateBill()}
              >
                <CgIcon name="billing" size={16} stroke={2} />
                {generating ? "Working…" : "Generate Bill"}
              </button>
            )}
            <button
              type="button"
              className={`${styles.btn} ${styles.btnP}`}
              disabled={generating || !hasItems}
              onClick={handleBarAction}
            >
              <CgIcon
                name={isIpSale ? "check" : "rupee2"}
                size={16}
                stroke={isIpSale ? 2.4 : 2}
              />
              {generating
                ? "Working…"
                : isIpSale
                  ? "Submit"
                  : "Generate Bill & Record Payment"}
            </button>
          </div>
        ) : undefined
      }
    >
      <div
        className={styles.wrap}
        style={{
          padding: 24,
          paddingTop: 0,
          paddingBottom: showActions || showSaveEdits ? 120 : 24,
        }}
      >
        {/* The way back is the arrow on the heading, not a line of its own
            above it - the title is what the screen is, and it should be the
            first thing on it. */}
        <div className={styles.phead}>
          <h1 className={styles.ptitle}>
            <button
              type="button"
              className={styles.pback}
              onClick={leaveEntry}
              title="Back to Orders"
              aria-label="Back to Orders"
            >
              {/* Sized and weighted off the heading it sits on: 23px title,
                  so a 20px mark at the title's own stroke. */}
              <CgIcon name="back" size={20} stroke={2.6} />
            </button>
            Sale Entry
          </h1>

          {/* The order as a document, once there is an order to draw one for:
              submitted, or at least dispensed against. Before that the entry is
              still being written and a receipt would say nothing. */}
          {/* The head is a title and the things that may be done to the
              order it names, kept together at its end - the row spaces its
              two halves apart, so a second button cannot be left loose in
              the middle of it. */}
          <div className={styles.pheadActions}>
            {order?.id && isPharmacyAdmin ? (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnG}`}
                onClick={() => setHistoryOpen(true)}
              >
                <CgIcon name="ledger" size={16} stroke={2} />
                Audit log
              </button>
            ) : null}

            {canPrintOrderReceipt(order) ? (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnG}`}
                disabled={printing}
                onClick={handlePrintReceipt}
              >
                <CgIcon name="download" size={16} stroke={2} />
                {printing ? "Working…" : "Download Receipt"}
              </button>
            ) : null}
          </div>
        </div>

        {/* Patient Details runs the full width; under it the working pair -
            the line grid at 70%, the price history and the bill at 30%. */}
        <div className={styles.seWrap}>
          <div className={styles.sePat}>
            <PatientDetailsCard
              ref={patientRef}
              order={order}
              onSaved={handlePatientDetailsSaved}
              onSaleTypeChange={setSaleType}
              onPatientChange={handlePatientChange}
              onCarriedBillChange={setCarriedBill}
              disabled={isLocked || Boolean(bill)}
            />
          </div>

          <div className={styles.seMain}>
            <MedicinesConsumablesCard
              ref={medsRef}
              order={order}
              onRefresh={refreshOrder}
              onEnsureOrder={ensureOrder}
              onTotalsChange={setPreview}
              onDraftChange={handleDraftChange}
              disabled={!linesEditable}
              /* Whose ledger the panel under a line reads. */
              historyPatientId={historyPatientId}
              historyPatientName={historyPatientName}
            />
          </div>

          {/*
            * What the tax comes to, slab by slab - the same table the purchase
            * screens print under their lines. Only where there is something to
            * tax: an entry with no medicines on it has no slabs, and an empty
            * table with headings over it reads as a figure of nothing rather
            * than as nothing to figure.
            */}
          {gstLines.length ? (
            <div className={styles.seGst}>
              <section className={styles.card}>
                <div className={styles.cardHead}>
                  <h3 className={styles.cardTitle}>GST Summary</h3>
                </div>
                <div className={styles.seGstBody}>
                  <GstSummaryTable items={gstLines} />
                </div>
              </section>
            </div>
          ) : null}

          <aside className={styles.seSide}>
            <SaleEntryBillingCard
              ref={billingRef}
              order={order}
              bill={bill}
              rawTotals={preview}
              carriedBill={carriedBill}
              onBillRefresh={refreshBill}
              onPayableChange={handlePayableChange}
              onGstLinesChange={setGstLines}
              stacked
            />
          </aside>
        </div>

        {/* The money, collected where the mockup collects it. A run that stops
            on a missing batch or an empty order leaves this open with the
            reason on screen rather than dropping the pharmacist back on the
            grid with nothing said. */}
        <AuditLogDrawer
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType={AUDIT_ENTITIES.order}
          entityId={order?.id}
          documentNumber={order?.order_number}
        />

        <DiscardEntryModal
          open={discardOpen}
          working={discarding}
          willDiscard={willDiscard}
          lineCount={draft.lines}
          patientName={historyPatientName}
          onKeepEditing={() => setDiscardOpen(false)}
          onSave={saveAndLeave}
          saving={saving}
          onDiscard={discardEntry}
        />

        <RecordPaymentModal
          open={payOpen}
          working={generating}
          due={payable.amount}
          subtitle={[
            historyPatientName,
            `${payable.lines} ${payable.lines === 1 ? "medicine" : "medicines"}`,
          ]
            .filter(Boolean)
            .join(" · ")}
          onCancel={() => setPayOpen(false)}
          onConfirm={async (payment) => {
            const done = await handleGenerateBill(payment);
            if (done) setPayOpen(false);
          }}
        />
      </div>
    </CustomLayout>
  );
};

export default SaleEntryPage;
